import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Download, FileText, LucideAngularModule, Pencil, Plus, Search } from 'lucide-angular';
import { catchError, of } from 'rxjs';
import { ApiService } from '../../../core/services/api.service';
import { ToastService } from '../../../shared/ui/toast.service';

interface CreditDebitNoteItem {
  id: string;
  note_type: string;
  note_date: string;
  client_name: string;
  invoice_id: string;
  total_amount: number | string;
  remarks?: string | null;
  created_at?: string;
  rows?: Array<{
    particulars?: string;
    quantity?: number | string;
    rate?: number | string;
    amount?: number | string;
  }>;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './credit-debit-note-list.page.html',
  styleUrl: './credit-debit-note-list.page.scss',
})
export class CreditDebitNoteListPage implements OnInit {
  private readonly api = inject(ApiService);
  private readonly http = inject(HttpClient);
  private readonly toast = inject(ToastService);
  protected readonly router = inject(Router);

  protected readonly FileTextIcon = FileText;
  protected readonly PlusIcon = Plus;
  protected readonly SearchIcon = Search;
  protected readonly PencilIcon = Pencil;
  protected readonly DownloadIcon = Download;

  protected readonly loading = signal(false);
  protected readonly search = signal('');
  protected readonly notes = signal<CreditDebitNoteItem[]>([]);

  protected readonly filteredNotes = computed(() => {
    const term = this.search().trim().toLowerCase();
    if (!term) return this.notes();
    return this.notes().filter((row) =>
      [
        row.id,
        row.note_type,
        row.note_date,
        row.client_name,
        row.invoice_id,
        row.remarks || '',
      ]
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  });

  ngOnInit(): void {
    this.fetchNotes();
  }

  protected onSearch(value: string): void {
    this.search.set(value);
  }

  protected formatAmount(value: number | string): string {
    const num = Number(value) || 0;
    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(num);
  }

  protected editNote(row: CreditDebitNoteItem): void {
    this.router.navigateByUrl(`/credit-debit-notes/${row.id}/edit`);
  }

  protected downloadNotePdf(row: CreditDebitNoteItem): void {
    const payload = {
      note_id: row.id,
      note_type: row.note_type || 'Credit Note',
      note_date: row.note_date || '',
      client_name: row.client_name || '-',
      invoice_id: row.invoice_id || '-',
      total_amount: Number(row.total_amount) || 0,
      remarks: row.remarks || '',
      rows: (row.rows || []).map((item) => ({
        particulars: item.particulars || '',
        quantity: Number(item.quantity) || 0,
        rate: Number(item.rate) || 0,
        amount: Number(item.amount) || 0,
      })),
    };

    this.http
      .post(this.api.resolve('/api/credit-debit-notes/download-pdf'), payload, {
        withCredentials: true,
        responseType: 'blob',
      })
      .pipe(catchError(() => of(null)))
      .subscribe((blob) => {
        if (blob) {
          this.downloadBlob(blob, `${row.id || 'credit-debit-note'}.pdf`);
          return;
        }
        this.downloadNoteHtml(row);
        this.toast.show('PDF endpoint unavailable', 'Downloaded invoice-style note HTML instead.', 'info');
      });
  }

  private fetchNotes(): void {
    this.loading.set(true);
    this.api
      .get<CreditDebitNoteItem[]>('/api/credit-debit-notes')
      .pipe(catchError(() => of([])))
      .subscribe((items) => {
        this.notes.set(items || []);
        this.loading.set(false);
      });
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  private downloadNoteHtml(row: CreditDebitNoteItem): void {
    const rows = row.rows || [];
    const rowsHtml = rows.length
      ? rows
          .map((item, index) => {
            const quantity = Number(item.quantity) || 0;
            const rate = Number(item.rate) || 0;
            const amount = Number(item.amount) || Number((quantity * rate).toFixed(2));
            return `<tr>
              <td>${index + 1}</td>
              <td>${this.escapeHtml(item.particulars || '-')}</td>
              <td>${quantity.toFixed(2)}</td>
              <td>${rate.toFixed(2)}</td>
              <td>Rs. ${amount.toFixed(2)}</td>
            </tr>`;
          })
          .join('')
      : `<tr><td>1</td><td>Note Amount</td><td>1.00</td><td>${(Number(row.total_amount) || 0).toFixed(2)}</td><td>Rs. ${(Number(row.total_amount) || 0).toFixed(2)}</td></tr>`;

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${this.escapeHtml(row.id)} - ${this.escapeHtml(row.note_type || 'Note')}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 16px; color: #111; }
    .title { text-align: center; font-size: 20px; font-weight: 700; margin-bottom: 8px; }
    .box { border: 1px solid #444; width: 100%; border-collapse: collapse; }
    .box td, .box th { border: 1px solid #444; padding: 6px; font-size: 12px; vertical-align: top; }
    .small { font-size: 11px; }
    .right { text-align: right; }
    .bold { font-weight: 700; }
    .mt8 { margin-top: 8px; }
  </style>
</head>
<body>
  <div class="title">${this.escapeHtml((row.note_type || 'Credit/Debit Note').toUpperCase())}</div>
  <table class="box">
    <tr>
      <td style="width:60%">
        <div class="bold">Client</div>
        <div>${this.escapeHtml(row.client_name || '-')}</div>
      </td>
      <td style="width:40%">
        <div><span class="bold">Note No:</span> ${this.escapeHtml(row.id || '-')}</div>
        <div><span class="bold">Note Date:</span> ${this.escapeHtml(row.note_date || '-')}</div>
        <div><span class="bold">Invoice:</span> ${this.escapeHtml(row.invoice_id || '-')}</div>
      </td>
    </tr>
  </table>

  <table class="box mt8">
    <tr>
      <th style="width:5%">S.No.</th>
      <th style="width:47%">Particulars</th>
      <th style="width:12%">Qty</th>
      <th style="width:16%">Rate</th>
      <th style="width:20%">Amount</th>
    </tr>
    ${rowsHtml}
    <tr>
      <td colspan="4" class="right bold">Total:</td>
      <td class="bold">Rs. ${(Number(row.total_amount) || 0).toFixed(2)}</td>
    </tr>
  </table>
  <div class="mt8"><span class="bold">Remarks:</span> ${this.escapeHtml(row.remarks || '-')}</div>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
    this.downloadBlob(blob, `${row.id || 'credit-debit-note'}.html`);
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
